/**
 * Intraday contract classification and timing helpers (FTAPI-204).
 *
 * Service cadence selects the UI independently of the trading availability window.
 * Nested start_trade_time and last_trade_time are epoch seconds used for scheduling only.
 */

/** Shape shared by API contracts and normalized expiration options. */
export interface IntradayTiming {
  measured_period?: string;
  interval_type?: string;
  start_trade_time?: number;
  last_trade_time?: number;
  contracts?: { start_trade_time?: number; last_trade_time?: number }[];
}

const FLAVOR_INTERVALS = {
  '15min': 15 * 60,
  hourly: 60 * 60,
} as const;

export type IntradayFlavor = keyof typeof FLAVOR_INTERVALS | 'other';

export interface IntradayClassification {
  flavor: IntradayFlavor;
  intervalSeconds: number;
}

/** Classifies explicit cadence, never inferring it from timestamps or display labels. */
export function classifyIntradayContract(cc: IntradayTiming): IntradayClassification | undefined {
  const cadence = cc.interval_type?.trim().toLowerCase();
  const numericCadence = cadence?.match(/^(\d+)\s*-?\s*(min(?:ute)?s?|hours?|h)$/);
  const intervalSeconds =
    cadence === 'hourly'
      ? 3600
      : numericCadence
        ? Number(numericCadence[1]) * (numericCadence[2].startsWith('h') ? 3600 : 60)
        : 0;
  if (intervalSeconds <= 0 || intervalSeconds >= 86400) return undefined;

  for (const flavor of Object.keys(FLAVOR_INTERVALS) as Array<keyof typeof FLAVOR_INTERVALS>) {
    if (intervalSeconds === FLAVOR_INTERVALS[flavor]) {
      return { flavor, intervalSeconds };
    }
  }

  return { flavor: 'other', intervalSeconds };
}

export function formatMeasuredPeriod(period: { measured_period?: string; interval_type?: string }): string {
  const label = period.measured_period?.trim() ?? '';
  const cadence = period.interval_type?.trim();
  if (!label || !cadence || label.toLowerCase().endsWith(`(${cadence.toLowerCase()})`)) return label;
  return `${label} (${cadence})`;
}

export function isIntradayExpiration(cc: IntradayTiming): boolean {
  return classifyIntradayContract(cc) !== undefined;
}

export function isHourlyExpiration(cc: IntradayTiming): boolean {
  return classifyIntradayContract(cc)?.flavor === 'hourly';
}

/** True when the given start time (epoch seconds) is a real time still in the future. */
export function isNotYetStarted(startTime?: number | null): boolean {
  if (startTime == null || !Number.isFinite(startTime) || startTime <= 0) return false;
  return startTime * 1000 > Date.now();
}
